import { StudentSubsidyProgram } from '../shared-typings/models/student-subsidy-program.model';

/**
 * Subsidy type enumeration based on backend implementation
 */
export enum SubsidyType {
  NONE = 'none',
  SCHOLARSHIP = 'scholarship', // NSDExempt = 1 (show $0, no org billing)
  PCOE = 'pcoe', // NSDExempt = 0, programName includes 'PCOE'
  OTHER = 'other', // NSDExempt = 0, other program name
  ELOP = 'elop', // From studentsSubsidyEligible relation
}

/**
 * Get the subsidy type for a student
 */
export function getSubsidyType(
  studentsSubsidyEligible: StudentSubsidyProgram | null | undefined
): SubsidyType {
  if (!studentsSubsidyEligible?.SubsidyProgram) {
    return SubsidyType.NONE;
  }

  const subsidyProgram = studentsSubsidyEligible.SubsidyProgram;
  const programName = subsidyProgram.programName?.toLowerCase() || '';

  // Check if it's a scholarship (NSDExempt = 1)
  if (subsidyProgram.NSDExempt) {
    return SubsidyType.SCHOLARSHIP;
  }

  // Check if it's PCOE (NSDExempt = 0 and name contains 'pcoe')
  if (programName.includes('pcoe')) {
    return SubsidyType.PCOE;
  }

  // Check if it's ELOP (NSDExempt = 0 and name contains 'elop' or 'elo-p')
  if (programName.includes('elop') || programName.includes('elo-p')) {
    return SubsidyType.ELOP;
  }

  // Otherwise it's Other (NSDExempt = 0, other name)
  return SubsidyType.OTHER;
}

/**
 * Get display information for subsidy type
 */
export function getSubsidyDisplayInfo(
  subsidyType: SubsidyType,
  orgName?: string
) {
  switch (subsidyType) {
    case SubsidyType.PCOE:
      return {
        label: 'Covered by PCOE',
        description: 'Organization Billed',
        color: 'blue' as const,
        showActualPrice: true,
        bypassPayment: true,
      };
    case SubsidyType.OTHER:
      return {
        label: orgName ? `Covered by ${orgName}` : 'Covered by Organization',
        description: 'Organization Billed',
        color: 'blue' as const,
        showActualPrice: true,
        bypassPayment: true,
      };
    case SubsidyType.SCHOLARSHIP:
      return {
        label: 'Scholarship',
        description: 'No Charge',
        color: 'green' as const,
        showActualPrice: false,
        bypassPayment: true,
      };
    case SubsidyType.ELOP:
      return {
        label: 'ELOP Funded',
        description: orgName ? `Billed to ${orgName}` : 'Organization Billed',
        color: 'purple' as const,
        showActualPrice: false,
        bypassPayment: true,
      };
    case SubsidyType.NONE:
    default:
      return {
        label: '',
        description: '',
        color: 'default' as const,
        showActualPrice: true,
        bypassPayment: false,
      };
  }
}

/**
 * Check if student should bypass payment
 */
export function shouldBypassPayment(
  studentsSubsidyEligible: StudentSubsidyProgram | null | undefined
): boolean {
  const subsidyType = getSubsidyType(studentsSubsidyEligible);
  return subsidyType !== SubsidyType.NONE;
}

/**
 * Get payment success message based on subsidy type
 */
export function getPaymentSuccessMessage(
  studentsSubsidyEligible: StudentSubsidyProgram | null | undefined,
  orgName?: string
): string {
  const subsidyType = getSubsidyType(studentsSubsidyEligible);

  switch (subsidyType) {
    case SubsidyType.PCOE:
    case SubsidyType.OTHER:
      return `No payment required - ${orgName || 'Organization'} will be billed`;
    case SubsidyType.ELOP:
      return `No payment required - ELOP funding applied`;
    case SubsidyType.SCHOLARSHIP:
      return 'No payment required - Scholarship applied';
    default:
      return 'No payment required';
  }
}
